/**
 * Page d'annulation d'achat de crédits
 * 
 * Cette page est affichée lorsque l'utilisateur annule le paiement Stripe.
 * Redirection automatique vers le dashboard après 5 secondes.
 * 
 * Requirements: 6.4
 */

import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export const CreditPurchaseCancel = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [countdown, setCountdown] = useState(5);
  const hasShownToast = useRef(false);

  useEffect(() => {
    // Afficher le toast d'information (une seule fois)
    if (!hasShownToast.current) {
      hasShownToast.current = true;
      toast({
        title: "Paiement annulé",
        description: "Aucun montant n'a été débité de votre compte.",
        variant: "default",
        duration: 5000,
      });
    }

    // Démarrer le compte à rebours
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Redirection automatique vers le dashboard après 5 secondes
    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, 5000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Dépendances vides pour n'exécuter qu'une seule fois

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="text-center max-w-md w-full">
        {/* Icône d'annulation */}
        <div className="flex justify-center mb-6">
          <div className="h-24 w-24 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <XCircle className="h-16 w-16 text-orange-600 dark:text-orange-400" />
          </div>
        </div>
        
        {/* Titre */}
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Paiement annulé
        </h1>
        
        {/* Message */}
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
          Votre achat de crédits a été annulé.
        </p>
        
        <p className="text-sm text-gray-500 dark:text-gray-500 mb-8">
          Aucun montant n'a été débité de votre compte.
        </p>
        
        {/* Compte à rebours */}
        <div className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 rounded-full shadow-lg">
          <span className="text-2xl animate-spin">⏳</span>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Redirection dans <span className="font-bold text-gray-900 dark:text-white">{countdown}</span> seconde{countdown > 1 ? 's' : ''}
          </p>
        </div>
      </div>
    </div>
  );
};
